import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCurrentSession, openSession, closeSession, addMovement, summarize, MOV_TYPES,
} from '@/lib/cashDrawer';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency, formatDateTime } from '@/lib/formatters';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Vault, ArrowDownToLine, ArrowUpFromLine, Lock, Unlock, AlertTriangle,
  CheckCircle2, Banknote, CreditCard, QrCode,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Card e fluxos completos de Abertura/Fechamento de Caixa.
 * Usado no header do CaixaPage.
 */
export default function CashDrawerCard({ onChange }) {
  const { operator } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOpen, setShowOpen] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showMov, setShowMov] = useState(null); // 'sangria' | 'suprimento' | null

  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const s = await getCurrentSession();
      setSession(s);
      onChangeRef.current?.(s);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[CashDrawerCard] refresh erro:', err?.message || err);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) return null;

  const sum = session ? summarize(session) : null;

  return (
    <>
      <div data-testid="cash-drawer-card" className={`relative overflow-hidden rounded-2xl mb-6 border-2 ${
        session ? 'bg-emerald-50/40 border-emerald-200' : 'bg-amber-50/40 border-amber-200'
      }`}>
        <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
            session ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'
          }`}>
            {session ? <Unlock size={22} strokeWidth={2} /> : <Lock size={22} strokeWidth={2} />}
          </div>

          <div className="flex-1 min-w-0">
            {session ? (
              <>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-0.5">
                  Caixa aberto • Sessão #{session.id}
                </p>
                <p className="font-heading text-base sm:text-lg font-bold text-slate-900 truncate">
                  {session.openedByName || 'Operador'} • Fundo {formatCurrency(session.initialFloat || 0)}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Aberto em {formatDateTime(session.openedAt)} • {sum?.commandsClosedCount || 0} comanda(s) fechada(s) •
                  Vendas: <strong className="text-slate-700">{formatCurrency(sum?.totalSales || 0)}</strong>
                </p>
              </>
            ) : (
              <>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 mb-0.5">
                  Caixa fechado
                </p>
                <p className="font-heading text-base sm:text-lg font-bold text-slate-900">
                  Abra o caixa antes de iniciar o atendimento
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Defina o fundo de troco para começar o turno. Vendas só são registradas com caixa aberto.
                </p>
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-2 sm:shrink-0">
            {session ? (
              <>
                <button
                  data-testid="cash-drawer-sangria-btn"
                  onClick={() => setShowMov('sangria')}
                  className="flex items-center gap-1.5 px-3 h-10 rounded-lg border-2 border-rose-200 bg-white text-rose-600 text-xs font-bold hover:bg-rose-50 hover:border-rose-300 transition-colors"
                  title="Sangria (retirar dinheiro)"
                >
                  <ArrowUpFromLine size={14} /> Sangria
                </button>
                <button
                  data-testid="cash-drawer-suprimento-btn"
                  onClick={() => setShowMov('suprimento')}
                  className="flex items-center gap-1.5 px-3 h-10 rounded-lg border-2 border-blue-200 bg-white text-blue-600 text-xs font-bold hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  title="Suprimento (adicionar dinheiro)"
                >
                  <ArrowDownToLine size={14} /> Suprimento
                </button>
                <button
                  data-testid="cash-drawer-close-btn"
                  onClick={() => setShowClose(true)}
                  className="flex items-center gap-1.5 px-4 h-10 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors"
                >
                  <Lock size={14} /> Fechar caixa
                </button>
              </>
            ) : (
              <button
                data-testid="cash-drawer-open-btn"
                onClick={() => setShowOpen(true)}
                className="flex items-center gap-2 px-5 h-10 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors"
              >
                <Vault size={16} /> Abrir caixa
              </button>
            )}
          </div>
        </div>

        {session && sum && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-emerald-100">
            <Stat label="Dinheiro" value={sum.cash} icon={<Banknote size={12} />} testid="cash-stat-cash" />
            <Stat label="Cartão" value={sum.card} icon={<CreditCard size={12} />} testid="cash-stat-card" />
            <Stat label="Pix" value={sum.pix} icon={<QrCode size={12} />} testid="cash-stat-pix" />
            <Stat label="Sangria" value={sum.sangria} icon={<ArrowUpFromLine size={12} />} testid="cash-stat-sangria" tone="rose" />
            <Stat label="Suprimento" value={sum.suprimento} icon={<ArrowDownToLine size={12} />} testid="cash-stat-suprimento" tone="blue" />
          </div>
        )}
      </div>

      <OpenSessionDialog
        open={showOpen}
        onOpenChange={setShowOpen}
        operator={operator}
        onDone={refresh}
      />
      <CloseSessionDialog
        open={showClose}
        onOpenChange={setShowClose}
        session={session}
        operator={operator}
        onDone={refresh}
      />
      <MovementDialog
        type={showMov}
        onClose={() => setShowMov(null)}
        operator={operator}
        onDone={refresh}
      />
    </>
  );
}

function Stat({ label, value, icon, testid, tone = 'emerald' }) {
  const toneCls = tone === 'rose'
    ? 'text-rose-700'
    : tone === 'blue'
      ? 'text-blue-700'
      : 'text-emerald-700';
  return (
    <div data-testid={testid} className="bg-white px-3 py-2.5">
      <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ${toneCls}`}>
        {icon} {label}
      </div>
      <p className="font-mono text-sm font-black text-slate-900 mt-0.5">{formatCurrency(value || 0)}</p>
    </div>
  );
}

function OpenSessionDialog({ open, onOpenChange, operator, onDone }) {
  const [floatValue, setFloatValue] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setFloatValue('');
  }, [open]);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const initialFloat = Number(String(floatValue).replace(',', '.')) || 0;
      await openSession({
        initialFloat,
        operatorId: operator?.id,
        operatorName: operator?.name,
      });
      toast.success('Caixa aberto!', {
        description: `Fundo de troco: ${formatCurrency(initialFloat)}`,
      });
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.message || 'Falha ao abrir caixa');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent data-testid="cash-drawer-open-dialog" className="max-w-md bg-white border-2 border-emerald-200 rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-emerald-700 flex items-center gap-2">
            <Vault size={22} /> Abrir Caixa
          </DialogTitle>
          <DialogDescription>
            Informe quanto há de <strong>fundo de troco</strong> em dinheiro físico no caixa neste momento.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
              Fundo inicial (R$)
            </label>
            <Input
              data-testid="cash-drawer-open-float"
              type="text"
              inputMode="decimal"
              placeholder="100,00"
              value={floatValue}
              onChange={(e) => setFloatValue(e.target.value.replace(/[^\d.,]/g, ''))}
              className="h-12 text-2xl font-mono font-bold text-center border-2 border-emerald-200 rounded-xl"
              autoFocus
            />
            <p className="text-[10px] text-slate-400 mt-1">Pode ser zero. Esse valor é a base pro fechamento.</p>
          </div>
          <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900">
            <p><strong>Operador:</strong> {operator?.name || '—'}</p>
            <p className="mt-0.5"><strong>Hora:</strong> {new Date().toLocaleString('pt-BR')}</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button
            data-testid="cash-drawer-open-cancel"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            data-testid="cash-drawer-open-confirm"
            onClick={handleConfirm}
            disabled={busy}
            className="flex-1 h-11 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? '...' : 'Abrir caixa'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MovementDialog({ type, onClose, operator, onDone }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const isSangria = type === 'sangria';

  useEffect(() => {
    if (type) { setAmount(''); setNote(''); }
  }, [type]);

  if (!type) return null;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const value = Number(String(amount).replace(',', '.'));
      if (!Number.isFinite(value) || value <= 0) {
        toast.error('Informe um valor válido');
        setBusy(false);
        return;
      }
      await addMovement({
        type: isSangria ? MOV_TYPES.SANGRIA : MOV_TYPES.SUPRIMENTO,
        amount: value,
        note: note.trim() || null,
        operatorId: operator?.id,
        operatorName: operator?.name,
      });
      toast.success(`${isSangria ? 'Sangria' : 'Suprimento'} registrado`, {
        description: `${formatCurrency(value)}${note ? ` — ${note}` : ''}`,
      });
      onClose();
      onDone?.();
    } catch (err) {
      toast.error(err?.message || 'Falha ao registrar movimento');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!type} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent
        data-testid={`cash-drawer-${type}-dialog`}
        className={`max-w-md bg-white rounded-2xl border-2 ${isSangria ? 'border-rose-200' : 'border-blue-200'}`}
      >
        <DialogHeader>
          <DialogTitle className={`font-heading text-xl font-bold flex items-center gap-2 ${isSangria ? 'text-rose-700' : 'text-blue-700'}`}>
            {isSangria ? <ArrowUpFromLine size={22} /> : <ArrowDownToLine size={22} />}
            {isSangria ? 'Sangria' : 'Suprimento'}
          </DialogTitle>
          <DialogDescription>
            {isSangria
              ? 'Retirada de dinheiro do caixa (depósito bancário, despesa, etc).'
              : 'Adição de dinheiro no caixa (reforço de troco, etc).'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
              Valor (R$)
            </label>
            <Input
              data-testid={`cash-drawer-${type}-amount`}
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
              className={`h-12 text-2xl font-mono font-bold text-center border-2 rounded-xl ${isSangria ? 'border-rose-200' : 'border-blue-200'}`}
              autoFocus
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
              Motivo (opcional)
            </label>
            <Input
              data-testid={`cash-drawer-${type}-note`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isSangria ? 'Ex: Depósito banco' : 'Ex: Reforço troco'}
              className="h-10 border-2 border-slate-200 rounded-lg"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button
            data-testid={`cash-drawer-${type}-cancel`}
            onClick={onClose}
            disabled={busy}
            className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            data-testid={`cash-drawer-${type}-confirm`}
            onClick={handleConfirm}
            disabled={busy}
            className={`flex-1 h-11 rounded-xl text-white font-bold text-sm disabled:opacity-50 ${isSangria ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {busy ? '...' : 'Registrar'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloseSessionDialog({ open, onOpenChange, session, operator, onDone }) {
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setCounted(''); setNote(''); }
  }, [open]);

  if (!session) return null;

  const sum = summarize(session);
  const countedNum = Number(String(counted).replace(',', '.')) || 0;
  const difference = countedNum - (sum?.expectedCash || 0);
  const diffSignal = difference === 0 ? 'zero' : difference > 0 ? 'positive' : 'negative';

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await closeSession({
        countedCash: countedNum,
        operatorId: operator?.id,
        operatorName: operator?.name,
        note: note.trim() || null,
      });
      toast.success(`Caixa fechado (sessão #${session.id})`, {
        description: difference === 0
          ? 'Bate certinho!'
          : `${difference > 0 ? 'Sobra' : 'Falta'} de ${formatCurrency(Math.abs(difference))}`,
      });
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.message || 'Falha ao fechar caixa');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent data-testid="cash-drawer-close-dialog" className="max-w-lg bg-white border-2 border-slate-300 rounded-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <Lock size={20} /> Fechar Caixa — Sessão #{session.id}
          </DialogTitle>
          <DialogDescription>
            Conte o dinheiro físico do caixa e digite o valor abaixo para fazer a conciliação.
          </DialogDescription>
        </DialogHeader>

        {/* Resumo da sessão */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1.5 text-sm">
          <RowKV label="Operador" value={session.openedByName || '—'} />
          <RowKV label="Aberto em" value={formatDateTime(session.openedAt)} />
          <div className="border-t border-slate-200 my-1.5" />
          <RowKV label="Fundo inicial" value={formatCurrency(sum.initialFloat)} mono />
          <RowKV label="(+) Vendas em dinheiro" value={formatCurrency(sum.cash)} mono />
          <RowKV label="(+) Suprimentos" value={formatCurrency(sum.suprimento)} mono />
          <RowKV label="(–) Sangrias" value={formatCurrency(sum.sangria)} mono tone="rose" />
          <div className="border-t border-slate-300 my-1.5" />
          <RowKV
            label="Esperado em dinheiro"
            value={formatCurrency(sum.expectedCash)}
            mono
            bold
          />
          <div className="border-t border-slate-200 my-1.5" />
          <RowKV label="Cartão (informativo)" value={formatCurrency(sum.card)} mono small />
          <RowKV label="Pix (informativo)" value={formatCurrency(sum.pix)} mono small />
          <RowKV label="Total vendido" value={formatCurrency(sum.totalSales)} mono small />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
            Dinheiro contado fisicamente (R$)
          </label>
          <Input
            data-testid="cash-drawer-close-counted"
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={counted}
            onChange={(e) => setCounted(e.target.value.replace(/[^\d.,]/g, ''))}
            className="h-14 text-3xl font-mono font-bold text-center border-2 border-slate-300 rounded-xl"
            autoFocus
          />
        </div>

        {counted && (
          <div
            data-testid="cash-drawer-close-diff"
            className={`px-4 py-3 rounded-xl border-2 flex items-center gap-3 ${
              diffSignal === 'zero' ? 'bg-emerald-50 border-emerald-300'
                : diffSignal === 'positive' ? 'bg-blue-50 border-blue-300'
                : 'bg-rose-50 border-rose-300'
            }`}
          >
            {diffSignal === 'zero' ? <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
              : <AlertTriangle size={20} className={diffSignal === 'positive' ? 'text-blue-600 shrink-0' : 'text-rose-600 shrink-0'} />
            }
            <div className="min-w-0 flex-1">
              <p className="font-bold text-sm text-slate-900">
                {diffSignal === 'zero' ? 'Caixa bate certinho!'
                  : diffSignal === 'positive' ? `Sobra: ${formatCurrency(difference)}`
                  : `Falta: ${formatCurrency(Math.abs(difference))}`}
              </p>
              <p className="text-[11px] text-slate-600 mt-0.5">
                Esperado {formatCurrency(sum.expectedCash)} • Contado {formatCurrency(countedNum)}
              </p>
            </div>
          </div>
        )}

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
            Observação (opcional)
          </label>
          <Input
            data-testid="cash-drawer-close-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ex: Diferença pq tive que dar troco em moedas..."
            className="h-10 border-2 border-slate-200 rounded-lg"
          />
        </div>

        <DialogFooter className="gap-2">
          <button
            data-testid="cash-drawer-close-cancel"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            data-testid="cash-drawer-close-confirm"
            onClick={handleConfirm}
            disabled={busy || !counted}
            className="flex-1 h-11 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? '...' : 'Fechar caixa'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RowKV({ label, value, mono, bold, tone, small }) {
  const valueCls = `${mono ? 'font-mono' : ''} ${bold ? 'font-bold text-slate-900' : 'text-slate-700'} ${tone === 'rose' ? 'text-rose-700' : ''} ${small ? 'text-xs' : ''}`;
  return (
    <div className={`flex justify-between ${small ? 'text-[11px]' : 'text-sm'}`}>
      <span className={`${small ? 'text-slate-400' : 'text-slate-600'}`}>{label}</span>
      <strong className={valueCls}>{value}</strong>
    </div>
  );
}
